// src/components/Application.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { Form, Select, Input, DatePicker, Button, notification } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { createApplication, updateApplication } from '../actions/applicationactions';
import { keycloak } from '../auth/keycloak';
import { selectTestbed } from '../slices/testbedSlice';
import './Application.css';

const TB_LABEL = { tbA: 'INTRA', tbB: 'TMA' };
const GROUP_TO_TBID = { '/testbed1': 'tbA', '/testbed2': 'tbB' };

export default function Application({ setApplicationId, isUpdate = false, initialData = null, onClose }) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();

  // current chosen testbed from /choose-testbed
  const tbId = useSelector(selectTestbed); // 'tbA' | 'tbB' | null

  // stable initialData reference
  const safeInit = useMemo(() => initialData || {}, [initialData]);

  // allowed testbeds from keycloak groups
  const allowedTbIds = useMemo(() => {
    const groups = keycloak.tokenParsed?.groups || [];
    const ids = groups.map(g => GROUP_TO_TBID[g]).filter(Boolean);
    return Array.from(new Set(ids)); // de-dup
  }, []);

  // Trial options (disable the non-selected if admin chose a tb in /choose-testbed)
  const trialOptions = useMemo(() => {
    const ids = allowedTbIds.length ? allowedTbIds : ['tbA', 'tbB'];
    return ids.map(id => ({
      value: id,
      label: TB_LABEL[id] || id,
      disabled: tbId ? id !== tbId : false, // disable the other option if tbId is set
    }));
  }, [allowedTbIds, tbId]);

  // initial Trial value
  const initialTrialValue = useMemo(() => {
    if (allowedTbIds.length === 1) return allowedTbIds[0];
    if (tbId) return tbId;
    return trialOptions[0]?.value;
  }, [allowedTbIds, tbId, trialOptions]);

  const [applicationData, setApplicationData] = useState({
    applicationName: safeInit.applicationName || '',
    description: safeInit.description || '',
    trial: safeInit.trial || initialTrialValue || '',
    responsible: safeInit.responsible || '',
    contact: safeInit.contact || '',
    date: safeInit.date || null,
  });

  // set form fields on initial load / when initial data or chosen tb changes
  useEffect(() => {
    setApplicationData(prev => ({
      ...prev,
      applicationName: safeInit.applicationName || '',
      description: safeInit.description || '',
      trial: safeInit.trial || initialTrialValue || '',
      responsible: safeInit.responsible || '',
      contact: safeInit.contact || '',
      date: safeInit.date || null,
    }));

    form.setFieldsValue({
      applicationName: safeInit.applicationName || '',
      description: safeInit.description || '',
      trial: safeInit.trial || initialTrialValue || '',
      ...(isUpdate ? {} : {
        responsible: safeInit.responsible || '',
        contact: safeInit.contact || '',
        date: safeInit.date || null,
      }),
    });
  }, [safeInit, initialTrialValue, isUpdate, form]);

  const handleChange = (field, value) => {
    setApplicationData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (values) => {
    if (isUpdate) {
      const id = safeInit.applicationId || safeInit.id;
      const updatedData = {
        applicationName: values.applicationName,
        description: values.description,
        trial: values.trial, // tbId
      };
      dispatch(updateApplication({ id, updatedData }))
        .unwrap()
        .then(() => {
          notification.success({
            message: 'Application Updated Successfully',
            description: 'The application information has been updated.',
          });
          onClose?.();
        })
        .catch((err) => {
          const errorMessage = typeof err === 'string' ? err : err?.error || 'There was an error updating the application.';
          notification.error({ message: 'Error Updating Application', description: errorMessage });
        });
      return;
    }

    const payload = {
      applicationName: values.applicationName,
      description: values.description,
      trial: values.trial, // tbId
      responsible: values.responsible,
      contact: values.contact,
      date: values.date,
    };

    dispatch(createApplication(payload))
      .then((response) => {
        const newId = response.payload.id;
        setApplicationId?.(newId);
        notification.success({
          message: 'Application Added Successfully',
          description: 'The application has been successfully created.',
        });
        onClose?.();
      })
      .catch(() => {
        notification.error({
          message: 'Error Adding Application',
          description: 'There was an error adding the application. Please try again.',
        });
      });
  };

  const singleChoice = allowedTbIds.length === 1; // non-admin with only one testbed

  return (
    <div className="application-section">
      <h4>{isUpdate ? 'Update Application Information' : 'Application'}</h4>

      <Form
        layout="vertical"
        onFinish={handleSubmit}
        form={form}
        initialValues={{
          applicationName: applicationData.applicationName,
          description: applicationData.description,
          trial: applicationData.trial || initialTrialValue,
          ...(isUpdate ? {} : {
            responsible: applicationData.responsible,
            contact: applicationData.contact,
            date: applicationData.date,
          }),
        }}
      >
        {/* Trial (auto-prefilled from choose-testbed; disable non-selected option) */}
        <div className="form-row">
          <Form.Item
            label="Select Trial"
            name="trial"
            className="form-item"
            rules={[{ required: true, message: 'Please select the testbed.' }]}
          >
            <Select
              // IMPORTANT: let Form control the value; no explicit `value` prop
              onChange={(val) => handleChange('trial', val)}
              disabled={singleChoice}          // lock if user only has one testbed
              options={trialOptions}            // other option disabled if tbId chosen
            />
          </Form.Item>
        </div>

        {isUpdate ? (
          <>
            <div className="form-row">
              <Form.Item
                label="Application Name"
                name="applicationName"
                className="form-item"
                rules={[{ required: true, message: 'Please input the application name!' }]}
              >
                <Input onChange={(e) => handleChange('applicationName', e.target.value)} />
              </Form.Item>
            </div>
            <div className="form-row">
              <Form.Item
                label="Description"
                name="description"
                className="form-item"
                rules={[{ required: true, message: 'Please input the description!' }]}
              >
                <Input onChange={(e) => handleChange('description', e.target.value)} />
              </Form.Item>
            </div>
          </>
        ) : (
          <>
            <div className="form-row">
              <Form.Item
                label="Application Name"
                name="applicationName"
                className="form-item"
                rules={[{ required: true, message: 'Please input the application name!' }]}
              >
                <Input onChange={(e) => handleChange('applicationName', e.target.value)} />
              </Form.Item>
              <Form.Item
                label="Responsible"
                name="responsible"
                className="form-item"
              >
                <Input onChange={(e) => handleChange('responsible', e.target.value)} />
              </Form.Item>
            </div>

            <div className="form-row">
              <Form.Item
                label="Description"
                name="description"
                className="form-item"
                rules={[{ required: true, message: 'Please input the description!' }]}
              >
                <Input onChange={(e) => handleChange('description', e.target.value)} />
              </Form.Item>
              <Form.Item
                label="Contact"
                name="contact"
                className="form-item"
              >
                <Input onChange={(e) => handleChange('contact', e.target.value)} />
              </Form.Item>
            </div>

            <div className="form-row">
              <Form.Item label="Date" name="date" className="form-item">
                <DatePicker onChange={(date) => handleChange('date', date)} />
              </Form.Item>
            </div>
          </>
        )}

        <Button type="primary" htmlType="submit">
          {isUpdate ? 'Update' : 'Submit'}
        </Button>
      </Form>
    </div>
  );
}
